import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Chip,
  alpha,
  InputAdornment,
  Divider,
  CircularProgress,
  Avatar,
} from '@mui/material';
import {
  Search,
  Clock,
  Briefcase,
  Users,
  FileText,
  Building2,
  Package,
  Calendar,
  DollarSign,
  Truck,
  ArrowRight,
  TrendingUp,
  Tag,
  User,
} from 'lucide-react';

// Mock search data - in a real app, this would come from an API
const mockSearchData = {
  jobs: [
    { id: 'J-2024-001', name: 'City Center Mall Signage', client: 'Westfield Corp', status: 'In Production', type: 'jobs' },
    { id: 'J-2024-002', name: 'Downtown Office Building', client: 'ABC Developers', status: 'Design', type: 'jobs' },
    { id: 'J-2024-003', name: 'Airport Terminal Signs', client: 'Metro Authority', status: 'Permit', type: 'jobs' },
    { id: 'J-2024-004', name: 'University Campus Wayfinding', client: 'State University', status: 'Active', type: 'jobs' },
    { id: 'J-2024-005', name: 'Hospital Exterior Signage', client: 'Regional Medical', status: 'Installation', type: 'jobs' },
  ],
  clients: [
    { id: 'C-001', name: 'Westfield Corporation', contact: 'John Smith', email: 'john@westfield.com', type: 'clients' },
    { id: 'C-002', name: 'ABC Developers Inc', contact: 'Sarah Johnson', email: 'sarah@abc.dev', type: 'clients' },
    { id: 'C-003', name: 'Metro Transit Authority', contact: 'Mike Wilson', email: 'mike@metro.gov', type: 'clients' },
    { id: 'C-004', name: 'State University', contact: 'Dr. Emily Brown', email: 'emily@state.edu', type: 'clients' },
  ],
  estimates: [
    { id: 'E-2024-050', name: 'Retail Store Package', client: 'Fashion Forward', amount: '$45,000', type: 'estimates' },
    { id: 'E-2024-051', name: 'Office Building Lobby', client: 'Tech Corp', amount: '$28,500', type: 'estimates' },
    { id: 'E-2024-052', name: 'Restaurant Signage Suite', client: 'Dine & Wine', amount: '$15,200', type: 'estimates' },
  ],
  inventory: [
    { id: 'INV-001', name: 'LED Channel Letters', stock: 45, category: 'Lighting', type: 'inventory' },
    { id: 'INV-002', name: 'Aluminum Composite Panel', stock: 120, category: 'Materials', type: 'inventory' },
    { id: 'INV-003', name: 'Vinyl Wrap - White', stock: 30, category: 'Materials', type: 'inventory' },
  ],
  staff: [
    { id: 'S-001', name: 'Mike Johnson', role: 'Project Manager', department: 'Operations', type: 'staff' },
    { id: 'S-002', name: 'Sarah Chen', role: 'Designer', department: 'Design', type: 'staff' },
    { id: 'S-003', name: 'Tom Williams', role: 'Installer', department: 'Installation', type: 'staff' },
  ],
};

const categoryConfig = {
  jobs: { icon: Briefcase, color: '#6366F1', label: 'Jobs' },
  clients: { icon: Building2, color: '#10B981', label: 'Clients' },
  estimates: { icon: DollarSign, color: '#F59E0B', label: 'Estimates' },
  inventory: { icon: Package, color: '#8B5CF6', label: 'Inventory' },
  staff: { icon: User, color: '#EF4444', label: 'Staff' },
};

function GlobalSearchDialog({ open, onClose, onNavigate }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState([
    'City Center Mall',
    'Westfield Corporation',
    'LED Channel Letters',
  ]);
  const inputRef = useRef(null);

  // Auto focus on open
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Reset state on close
  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setSelectedIndex(0);
    }
  }, [open]);

  // Search results with fuzzy matching
  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return [];

    setIsSearching(true);
    const term = searchTerm.toLowerCase();
    const results = [];

    // Search through all categories
    Object.entries(mockSearchData).forEach(([category, items]) => {
      items.forEach((item) => {
        const searchFields = [
          item.name,
          item.client,
          item.contact,
          item.id,
          item.status,
          item.category,
          item.role,
          item.department,
        ].filter(Boolean);

        const matchScore = searchFields.reduce((score, field) => {
          const fieldLower = field.toLowerCase();
          if (fieldLower === term) return score + 100;
          if (fieldLower.startsWith(term)) return score + 50;
          if (fieldLower.includes(term)) return score + 25;
          return score;
        }, 0);

        if (matchScore > 0) {
          results.push({ ...item, matchScore });
        }
      });
    });

    // Sort by match score
    results.sort((a, b) => b.matchScore - a.matchScore);

    setTimeout(() => setIsSearching(false), 150);
    return results.slice(0, 10);
  }, [searchTerm]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < searchResults.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : searchResults.length - 1
        );
      } else if (e.key === 'Enter' && searchResults[selectedIndex]) {
        e.preventDefault();
        handleSelect(searchResults[selectedIndex]);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, searchResults, selectedIndex]);

  const handleSelect = (item) => {
    // Add to recent searches
    setRecentSearches((prev) => {
      const filtered = prev.filter((s) => s !== item.name);
      return [item.name, ...filtered].slice(0, 5);
    });

    // Navigate to the item
    if (onNavigate) {
      onNavigate(item.type);
    }
    onClose();
  };

  const handleRecentSearch = (term) => {
    setSearchTerm(term);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          mt: -10,
        },
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <TextField
          inputRef={inputRef}
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search jobs, clients, estimates, inventory..."
          autoComplete="off"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {isSearching ? (
                  <CircularProgress size={20} />
                ) : (
                  <Search size={20} color="#64748B" />
                )}
              </InputAdornment>
            ),
            sx: {
              borderRadius: 3,
              bgcolor: alpha('#F1F5F9', 0.5),
              '& fieldset': { border: 'none' },
            },
          }}
        />
      </Box>

      <DialogContent sx={{ p: 0, maxHeight: 400 }}>
        {searchTerm.trim() ? (
          // Search Results
          <List sx={{ py: 1 }}>
            {searchResults.length > 0 ? (
              searchResults.map((result, idx) => {
                const config = categoryConfig[result.type];
                const Icon = config.icon;
                const isSelected = idx === selectedIndex;

                return (
                  <ListItem
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleSelect(result)}
                    sx={{
                      px: 2,
                      py: 1.5,
                      cursor: 'pointer',
                      bgcolor: isSelected ? alpha('#6366F1', 0.08) : 'transparent',
                      borderLeft: isSelected
                        ? `3px solid ${config.color}`
                        : '3px solid transparent',
                      transition: 'all 0.15s',
                      '&:hover': {
                        bgcolor: alpha('#6366F1', 0.05),
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 44 }}>
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: alpha(config.color, 0.1),
                        }}
                      >
                        <Icon size={18} color={config.color} />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" fontWeight={600}>
                            {result.name}
                          </Typography>
                          <Chip
                            label={result.id}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              bgcolor: alpha(config.color, 0.08),
                              color: config.color,
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {result.client || result.contact || result.category || result.role} • {config.label}
                        </Typography>
                      }
                    />
                    <ArrowRight size={16} color="#94A3B8" />
                  </ListItem>
                );
              })
            ) : (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Search size={40} color="#CBD5E1" />
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  No results found for "{searchTerm}"
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Try searching for job names, client names, or IDs
                </Typography>
              </Box>
            )}
          </List>
        ) : (
          // Recent Searches & Quick Actions
          <Box sx={{ p: 2 }}>
            {/* Recent Searches */}
            {recentSearches.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ px: 1, fontWeight: 600 }}
                >
                  Recent Searches
                </Typography>
                <List sx={{ mt: 1 }}>
                  {recentSearches.map((search, idx) => (
                    <ListItem
                      key={idx}
                      onClick={() => handleRecentSearch(search)}
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: alpha('#6366F1', 0.05) },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Clock size={16} color="#94A3B8" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={500}>
                            {search}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Quick Links */}
            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ px: 1, fontWeight: 600 }}
              >
                Quick Jump
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  mt: 1.5,
                  px: 1,
                }}
              >
                {Object.entries(categoryConfig).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <Chip
                      key={key}
                      icon={<Icon size={14} />}
                      label={config.label}
                      onClick={() => onNavigate?.(key)}
                      sx={{
                        cursor: 'pointer',
                        fontWeight: 600,
                        '& .MuiChip-icon': { color: config.color },
                        '&:hover': {
                          bgcolor: alpha(config.color, 0.08),
                        },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>

            {/* Search Tips */}
            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 2,
                bgcolor: alpha('#6366F1', 0.04),
                border: '1px solid',
                borderColor: alpha('#6366F1', 0.1),
              }}
            >
              <Typography variant="caption" color="text.secondary">
                <strong>Tip:</strong> Type job IDs (J-2024-001), client names, or
                any keyword to search across all data. Use arrow keys to navigate
                and Enter to select.
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default GlobalSearchDialog;
